import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { apiFetch, getStoredAuth, setStoredAuth, type StoredAuth } from './api-client'
import type { AuthUser, Role } from '@/types'

interface LoginResponse {
  token: string
  role: Role
  user: AuthUser
}

interface MePayload {
  id: number
  name: string
  email: string
  role: Role
  avatar_url: string | null
}

interface AuthContextValue {
  auth: StoredAuth | null
  isAdmin: boolean
  isSeller: boolean
  avatarUrl: string | null
  showQuickActions: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  dismissQuickActions: () => void
  updateUserName: (name: string) => void
  updateAvatar: (avatarUrl: string) => void
  updateToken: (token: string) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<StoredAuth | null>(() => getStoredAuth())
  const [showQuickActions, setShowQuickActions] = useState(false)

  useEffect(() => {
    if (!auth) return
    // Sincroniza nombre/avatar desde el servidor (pueden haber cambiado en otra sesión/dispositivo).
    apiFetch<MePayload>('/settings/me')
      .then((me) => {
        setAuth((current) =>
          current
            ? { ...current, role: me.role, user: { ...current.user, name: me.name, avatar_url: me.avatar_url } }
            : current,
        )
      })
      .catch(() => {
        // Silencioso: un token inválido/expirado ya se maneja globalmente en apiFetch.
      })
    // Solo al montar: sincroniza una vez por carga de la app, no en cada cambio de auth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setStoredAuth(auth)
  }, [auth])

  const value = useMemo<AuthContextValue>(
    () => ({
      auth,
      isAdmin: auth?.role === 'admin',
      isSeller: auth?.role === 'seller',
      avatarUrl: auth?.user.avatar_url ?? null,
      showQuickActions,
      login: async (email, password) => {
        const response = await apiFetch<LoginResponse>('/auth/login', {
          method: 'POST',
          body: { email, password },
        })
        setAuth({ token: response.token, role: response.role, user: response.user })
        setShowQuickActions(true)
      },
      logout: () => {
        setAuth(null)
        setShowQuickActions(false)
      },
      dismissQuickActions: () => setShowQuickActions(false),
      updateUserName: (name) => {
        setAuth((current) => (current ? { ...current, user: { ...current.user, name } } : current))
      },
      updateAvatar: (avatarUrl) => {
        setAuth((current) =>
          current ? { ...current, user: { ...current.user, avatar_url: avatarUrl } } : current,
        )
      },
      updateToken: (token) => {
        setAuth((current) => (current ? { ...current, token } : current))
      },
    }),
    [auth, showQuickActions],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>')
  return ctx
}
